<template>
    <flex-container>
        <button-block @click="onCreateClicked">
            Nieuwe licentie aanmaken
        </button-block>
        <button-block
            :color="'orange-600'"
            without-loading
            @click="shutDownAssistents?.toggleOverlay"
        >
            Schakel alle AI Agents uit
        </button-block>
        <list-container>
            <template #header>
                <list-cell-header>
                    <template #title>
                        Status
                    </template>
                </list-cell-header>
                <list-cell-header>
                    <template #title>
                        Uitgezet op
                    </template>
                    <template #subtitle>
                        Aangezet op
                    </template>
                </list-cell-header>
                <list-cell-header>
                    <template #title>
                        Uitgezet door
                    </template>
                </list-cell-header>
                <list-cell-header>
                    <template #title>
                        Reden
                    </template>
                </list-cell-header>
                <list-cell-header />
            </template>
            <template #content>
                <list-row v-for="killSwitch in sortedKillSwitches" :key="killSwitch.id">
                    <list-cell-icon
                        v-if="killSwitch.isResurrected()"
                        :icon="SatisfiedIcon"
                        :color="'green-500'"
                    />
                    <list-cell-icon
                        v-else
                        :icon="DissatisfiedIcon"
                        :color="'red-500'"
                    />
                    <list-cell-text :color="killSwitch.resurrectedAt === null ? 'orange-500' : 'green-300'">
                        <template #icons>
                            <flex-container
                                direction="column"
                                justify-content="center"
                                :gap="Size.xs"
                            >
                                <svg-icon
                                    :icon="SkullIcon"
                                    :size="IconSize.sm"
                                    :color="'red-500'"
                                />
                                <svg-icon
                                    :icon="ResumeIcon"
                                    :size="IconSize.sm"
                                    :color="'green-500'"
                                />
                            </flex-container>
                        </template>
                        <template #content>
                            <flex-container
                                direction="column"
                                justify-content="center"
                                :gap="Size.xs"
                            >
                                <text-decorator>
                                    {{ formatDateTime(killSwitch.killedAt) }}
                                </text-decorator>
                                <text-decorator>
                                    {{ killSwitch.resurrectedAt ? formatDateTime(killSwitch.resurrectedAt) : 'Kill switch nog steeds actief!' }}
                                </text-decorator>
                            </flex-container>
                        </template>
                    </list-cell-text>
                    <list-cell-text>
                        <template #content>
                            <text-decorator v-if="killSwitch.killedBy">
                                {{ killSwitch.killedBy.name }}
                            </text-decorator>
                        </template>
                    </list-cell-text>
                    <list-cell-text>
                        <template #content>
                            <text-decorator>
                                {{ killSwitch.reason }}
                            </text-decorator>
                        </template>
                    </list-cell-text>
                    <list-cell-default>
                        <list-cell-icon
                            v-if="killSwitch.resurrectedAt === null"
                            :icon="ResumeIcon"
                            :color="'green-500'"
                            @click="resurrectOpenAIAssistent"
                        />
                    </list-cell-default>
                </list-row>
            </template>
        </list-container>
    </flex-container>

    <form-validation v-slot="{ bindings: validationBindings }">
        <modal-dialog
            ref="shutDownAssistents"
            label="Shutdown modal"
            description="Modal to shut down all the AI Agents"
            :size="5"
        >
            <template #default>
                <flex-container>
                    <text-heading>Weet je dit zeker?</text-heading>
                    <p>
                        Vanuit hier kan je alle AI Agents uitzetten voor alle bedrijven.
                        Dit is handig wanneer OpenAI een storing heeft om foutieve gesprekken te voorkomen.
                        Alleen gebruiken wanneer uiterst noodzakelijk!
                    </p>
                    <flex-container>
                        <input-group
                            v-model="reasonOfKillSwitch"
                            :schema="string().required()"
                        >
                            <template #label>
                                <input-label>
                                    Reden
                                </input-label>
                            </template>
                            <template #input>
                                <text-input placeholder="Plaats hier de reden" />
                            </template>
                        </input-group>
                    </flex-container>
                </flex-container>
            </template>
            <template #footer="{ bindings }">
                <dialog-footer>
                    <button-block without-loading @click="bindings.overlay.closeOverlay">
                        Annuleren
                    </button-block>
                    <button-block-confirm
                        :color="'red-500'"
                        :disabled="!validationBindings.isValid.value"
                        @confirmed="killSwitchConfirmed($event, bindings)"
                    >
                        Shutdown OpenAI
                        <template #confirmation>
                            Zeker?
                        </template>
                    </button-block-confirm>
                </dialog-footer>
            </template>
        </modal-dialog>
    </form-validation>
</template>

<script setup lang="ts">
    import { useRouter } from 'vue-router';
    import { KillOpenAICommand, KillSwitch, ResurrectOpenAICommand } from '@keyvue/one-api-client';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { computed, onMounted, ref } from 'vue';
    import { formatDateTime } from '@/shared/utils/date';
    import SkullIcon from '@keyvue/one-material-icons/icons/skull.svg?component';
    import ResumeIcon from '@keyvue/one-material-icons/icons/resume.svg?component';
    import SatisfiedIcon from '@keyvue/one-material-icons/icons/sentiment_satisfied.svg?component';
    import DissatisfiedIcon from '@keyvue/one-material-icons/icons/sentiment_dissatisfied.svg?component';
    import {
        ButtonState,
        type ButtonStateChangedEvent,
        type ExposedOverlay,
        IconSize,
        Size,
    } from '@keyvue/one-ui';
    import { string } from 'yup';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';
    import { AxiosError } from 'axios';

    const router = useRouter();

    const { client, currentUser, getLazyResourceCollection } = useClient();
    const { notifyError } = useNotify();
    const shutDownAssistents = ref<ExposedOverlay>();

    const reasonOfKillSwitch = ref<string | null>(null);

    const killswitchCollection = await getLazyResourceCollection(KillSwitch);

    const sortedKillSwitches = computed(() => {
        return [...killswitchCollection.data.value].sort((a, b) => (new Date(b.killedAt)).valueOf() - (new Date(a.killedAt)).valueOf());
    });

    async function onCreateClicked() {
        await router.push({
            name: 'license-create',
        });
    }

    const killSwitchConfirmed = (event: ButtonStateChangedEvent, bindings: any) => {
        onKillSwitchActivated();
        event.button.setState(ButtonState.completed);
        bindings.overlay.closeOverlay();
    };

    async function onKillSwitchActivated() {
        if (reasonOfKillSwitch.value === null) {
            return;
        }

        try {
            await client.dispatch(
                new KillOpenAICommand(
                    currentUser.value,
                    reasonOfKillSwitch.value,
                    new Date(),
                ),
            );
        } catch (error) {
            if (error instanceof AxiosError) {
                notifyError(error?.response?.data ?? 'Er is iets fout gegaan');
            }
        }
        await killswitchCollection.load();
    }

    async function resurrectOpenAIAssistent() {
        await client.dispatch(
            new ResurrectOpenAICommand(
                new Date(),
            ),
        );
        await killswitchCollection.load();
    }

    onMounted(async () => {
        await killswitchCollection.load();
    });
</script>
